import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootDispatch, RootState } from 'storage/store';

type ContractAgreementDetail = {
  agreementId: number;
  contractId?: number;
  isOffer?: boolean;
  amountOutdated?: boolean;
};

export const useContractOrAgreement = () => {
  const dispatch = useDispatch<RootDispatch>();
  const { t } = useTranslation();

  const contractsDetailsToggled = useSelector(
    (state: RootState) => state.application?.contractDetailsFeatureToggle
  );

  const contractsToggled = useSelector(
    (state: RootState) => state.application?.contractDashboardFeatureToggle
  );

  const contractAgreements = useSelector(
    (state: RootState) => state.contractsAgreements
  );
  const regularAgreements = useSelector((state: RootState) => state.agreements);

  // not sure whether these agreements are included in changes
  const { agreementsWaitingForContract } = useSelector(
    (state: RootState) => state.agreements
  );

  const loadAgreementsOrContracts = async () => {
    if (contractsToggled) {
      await dispatch.contracts.getAllContracts();
      await dispatch.notifications.getNotifications();
    } else {
      await dispatch.agreements.getAllAgreements(false);
    }
  };

  const asyncLoadDetail = async ({
    agreementId,
    contractId,
    isOffer = false,
    amountOutdated = false
  }: ContractAgreementDetail) => {
    if (contractsDetailsToggled && contractId) {
      await dispatch.contracts
        .getContractById({
          contractId,
          agreementId,
          t
        })
        .then((agreement) => {
          if (agreement && !amountOutdated) {
            loadContractHistoryPayments(contractId, agreementId);
          }
        });
    }
    if (!contractsDetailsToggled || isOffer) {
      await dispatch.agreements.getAgreement({
        agreementId,
        audit: false
      });
    }
  };

  const loadContractHistoryPayments = async (
    contractId?: number,
    agreementId?: number
  ) => {
    if (!contractId || !agreementId) {
      return;
    }
    if (contractsDetailsToggled) {
      await dispatch.contracts.getContractHistoryPayments({
        contractId,
        sourceContractId: agreementId
      });
    }
  };

  const loadAvailability = async () => {
    if (!contractsToggled) {
      await dispatch.agreements.getAgreementsWithProductAvailability();
    }
  };

  const getAgreementDetails = (agreementId: number) =>
    contractsDetailsToggled
      ? contractAgreements[agreementId]
      : regularAgreements[agreementId];

  const {
    agreements: contracts,
    totalDebtAmountOnAllAgreementsByCurrency: totalContracts,
    agreementsWithProductAvailability: contractAvailable
  } = contractAgreements;

  const {
    agreements,
    agreementsWithProductAvailability,
    totalDebtAmountOnAllAgreementsByCurrency
  } = regularAgreements;

  const contractToggledReturn = {
    agreements: contracts,
    totalDebtAmountOnAllAgreementsByCurrency: totalContracts,
    agreementsWithProductAvailability: contractAvailable,
    agreementsWaitingForContract,
    getAgreementDetails,
    asyncLoadDetail,
    loadAvailability,
    loadAgreementsOrContracts,
    loadContractHistoryPayments
  };

  const regularReturn = {
    agreements,
    totalDebtAmountOnAllAgreementsByCurrency,
    agreementsWithProductAvailability,
    agreementsWaitingForContract,
    getAgreementDetails,
    asyncLoadDetail,
    loadAvailability,
    loadAgreementsOrContracts,
    loadContractHistoryPayments
  };
  if (contractsToggled) {
    return contractToggledReturn;
  }
  return regularReturn;
};
